import { Button, Dialog, Empty, NavBar, SearchBar, Toast } from 'antd-mobile';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../icon';
import { authApi } from '../../features/auth/api/authApi';
import './index.css';

const FILTER_OPTIONS = [
    { label: '默认', value: 0, icon: '👤' },
    { label: '绿衣', value: 1, icon: '🟢' },
    { label: '红衣', value: 2, icon: '🔴' },
    { label: '紫衣', value: 5, icon: '🟣' },
    { label: '裁判', value: 3, icon: '🪪' },
    { label: '门将', value: 4, icon: '🧤' },
];

export default function AvatarAudit() {
    const navigate = useNavigate();
    const [userListInfo, setUserListInfo] = useState([]);
    const [headerColor, setHeaderColor] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [onlyPending, setOnlyPending] = useState(false);

    const getAuditMembers = useCallback(async () => {
        Toast.show({ icon: 'loading', duration: 0, content: '加载中...' });
        try {
            const response = await authApi.getAuditMembers();
            setUserListInfo(response.data?.data || []);
        } catch (error) {
            Toast.show({ icon: 'fail', content: '头像审核数据加载失败' });
        } finally {
            Toast.clear();
        }
    }, []);

    useEffect(() => {
        getAuditMembers();
    }, [getAuditMembers]);

    const validUserList = useMemo(
        () => (userListInfo || []).filter(user => !user.tempMember),
        [userListInfo]
    );

    const getPendingImages = useCallback(
        user => (user?.scMemberImageList || []).filter(item => Number(item.status) === 1),
        []
    );

    const pendingImageCount = useMemo(
        () => validUserList.reduce((total, user) => total + getPendingImages(user).length, 0),
        [getPendingImages, validUserList]
    );

    const pendingMemberCount = useMemo(
        () => validUserList.filter(user => getPendingImages(user).length > 0).length,
        [getPendingImages, validUserList]
    );

    const currentType = FILTER_OPTIONS.find(item => item.value === headerColor);
    const currentTypePendingCount = useMemo(
        () => validUserList.reduce((total, user) => (
            total + (getPendingImages(user).some(item => Number(item.type) === headerColor) ? 1 : 0)
        ), 0),
        [getPendingImages, headerColor, validUserList]
    );

    const visibleUserList = useMemo(() => {
        const keyword = searchText.trim().toLowerCase();
        return validUserList
            .filter(user => !onlyPending || getPendingImages(user).length > 0)
            .filter(user => !keyword || String(user?.name || '').toLowerCase().includes(keyword))
            .sort((a, b) => getPendingImages(b).length - getPendingImages(a).length);
    }, [getPendingImages, onlyPending, searchText, validUserList]);

    const handleAuditAll = status => {
        const actionText = status === 0 ? '通过' : '驳回';
        Dialog.confirm({
            title: `全部${actionText}`,
            content: `确定将当前全部 ${pendingImageCount} 张待审核头像${actionText}吗？`,
            confirmText: `确认${actionText}`,
            cancelText: '取消',
            onConfirm: async () => {
                const formData = new FormData();
                formData.append('status', status);
                Toast.show({ icon: 'loading', duration: 0, content: '处理中...' });
                try {
                    const response = await authApi.auditAllImages(formData);
                    if (response.data?.code !== 200) {
                        throw new Error(response.data?.msg || '批量审核失败');
                    }
                    Toast.show({ content: '批量审核已完成', icon: 'success' });
                    await getAuditMembers();
                } catch (error) {
                    Toast.show({
                        content: error?.response?.data?.msg || error?.message || '批量审核失败',
                        icon: 'fail',
                    });
                }
            },
        });
    };

    const renderUserCard = user => {
        const pendingImages = getPendingImages(user);
        const currentAuditImage = pendingImages.find(item => Number(item.type) === headerColor);
        const displayImage = currentAuditImage
            || user?.scMemberImageList?.find(item => Number(item.type) === headerColor && Number(item.status) === 0)
            || user?.scMemberImageList?.find(item => Number(item.type) === headerColor);
        const imageSrc = displayImage?.image ? `${displayImage.image}${Icon.ImageFooter}` : Icon.KEPENG_LOGO;

        return (
            <button
                type="button"
                data-member-id={user.id}
                className={`aa-card ${pendingImages.length > 0 ? 'has-pending' : ''} ${currentAuditImage ? 'is-current-pending' : ''}`}
                key={user.id}
                onClick={() => navigate(`/avatarMember?userId=${user.id}`, { state: { userId: user.id } })}
            >
                <span className="aa-avatar-wrapper">
                    <img src={imageSrc} className="aa-avatar" alt={user.name} loading="lazy" />
                    {pendingImages.length > 0 && (
                        <i className="aa-pending-badge">{pendingImages.length > 99 ? '99+' : pendingImages.length}</i>
                    )}
                </span>
                <span className="aa-card-copy">
                    <strong>{user.name}</strong>
                    <small>{currentAuditImage ? `${currentType?.label}头像待审核` : `${pendingImages.length} 张待审核`}</small>
                </span>
                <span className={`aa-card-status ${currentAuditImage ? 'waiting' : pendingImages.length > 0 ? 'pending' : ''}`}>
                    {currentAuditImage ? '待处理' : pendingImages.length > 0 ? '其他类型待审' : '已完成'}
                </span>
                <span className="aa-card-arrow" aria-hidden="true">›</span>
            </button>
        );
    };

    return (
        <div className="aa-container has-unified-nav ui-page ui-page--with-nav">
            <NavBar className="unified-navbar fixed" onBack={() => window.history.back()}>
                球员头像审核
            </NavBar>

            <main className="aa-content">
                <section className="aa-hero">
                    <div className="aa-hero-copy">
                        <span className="aa-hero-eyebrow">头像审核中心</span>
                        <strong>{pendingImageCount > 0 ? '有新头像等待处理' : '头像审核已全部完成'}</strong>
                        <p>{pendingImageCount > 0 ? '待审内容已优先排列，点击球员即可查看原图并处理。' : '目前没有新的头像申请，可以查看已有球员头像。'}</p>
                    </div>
                    <div className={`aa-hero-focus ${pendingImageCount === 0 ? 'is-clear' : ''}`}>
                        <strong>{pendingImageCount}</strong>
                        <span>待审核</span>
                    </div>
                    <div className="aa-stats">
                        <div><strong>{pendingMemberCount}</strong><span>涉及球员</span></div>
                        <div><strong>{validUserList.length}</strong><span>正式球员</span></div>
                        <div><strong>{currentTypePendingCount}</strong><span>{currentType?.label}待审</span></div>
                    </div>
                </section>

                <section className="aa-filter-card">
                    <div className="aa-section-title">
                        <div><strong>预览头像类型</strong><span>待审数量会显示在对应类型中</span></div>
                    </div>
                    <div className="aa-filter-grid">
                        {FILTER_OPTIONS.map(option => {
                            const count = validUserList.reduce((total, user) => (
                                total + (getPendingImages(user).some(item => Number(item.type) === option.value) ? 1 : 0)
                            ), 0);
                            return (
                                <button
                                    type="button"
                                    key={option.value}
                                    className={`aa-filter-chip ${headerColor === option.value ? 'active' : ''}`}
                                    onClick={() => setHeaderColor(option.value)}
                                >
                                    <span>{option.icon}</span>
                                    <strong>{option.label}</strong>
                                    {count > 0 && <i>{count}</i>}
                                </button>
                            );
                        })}
                    </div>
                </section>

                <section className="aa-search-card">
                    <SearchBar
                        value={searchText}
                        onChange={setSearchText}
                        placeholder="搜索球员姓名"
                        className="aa-search"
                    />
                    <button
                        type="button"
                        className={`aa-pending-toggle ${onlyPending ? 'active' : ''}`}
                        onClick={() => setOnlyPending(value => !value)}
                    >
                        <span>{onlyPending ? '✓' : ''}</span>
                        只看待审
                    </button>
                </section>

                <div className="aa-list-header">
                    <div><strong>球员头像</strong><span>当前显示 {visibleUserList.length} 人，待审核球员优先</span></div>
                </div>

                {visibleUserList.length ? (
                    <div className="aa-grid">{visibleUserList.map(renderUserCard)}</div>
                ) : (
                    <div className="aa-empty"><Empty description="没有符合条件的球员" /></div>
                )}
            </main>

            {pendingImageCount > 0 && (
                <div className="aa-actions-footer">
                    {/* 批量审核保持原有业务接口，只优化操作层级与结果刷新方式。 */}
                    <Button className="aa-btn-success" block onClick={() => handleAuditAll(0)}>全部通过</Button>
                    <Button className="aa-btn-warning" block onClick={() => handleAuditAll(2)}>全部驳回</Button>
                </div>
            )}
        </div>
    );
}
